"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MyDealerConfigurationServiceV2Impl = void 0;
class MyDealerConfigurationServiceV2Impl {
    constructor(options, historyService) {
        var _a;
        this.options = options;
        this.historyService = historyService;
        this.listeners = [];
        this._initialPageOwnerConfig = options.initialPageOwner
            ? this.createDealerConfiguration(options.initialPageOwner.dealerId, options.initialPageOwner.config)
            : undefined;
        this.initialPath = this.getCurrentDealerHome();
        // new: preview during SSR is ok IF AND ONLY IF we are on the preview domain
        this.preview = Boolean(options.preview);
        // preview should only be true in the browser to make sure preview content is not cached in dispatcher
        if (typeof window !== 'undefined' &&
            !this.preview &&
            options.previewRequestParam) {
            this.preview =
                new URLSearchParams(window.location.search).get('preview') != null;
        }
        let currentPageOwner = (_a = this.initialPageOwnerConfig) === null || _a === void 0 ? void 0 : _a.id;
        historyService.rootHistory.listen(() => {
            const dealerHome = this.getCurrentDealerHome();
            if (Boolean(this.initialPath) && this.initialPath !== dealerHome) {
                this.initialPath = undefined;
            }
            this.getDealerPageOwner()
                .then((pageOwner) => {
                if (currentPageOwner !== pageOwner) {
                    currentPageOwner = pageOwner;
                    this.listeners.forEach((listener) => listener(currentPageOwner));
                }
            })
                .catch((e) => console.error('cannot get owner', e));
        });
    }
    get initialPageOwnerConfig() {
        return !!this.initialPath ? this._initialPageOwnerConfig : undefined;
    }
    getDealerPageOwner() {
        return this.getOwnerByPath(this.historyService.rootHistory.location.pathname);
    }
    async getDealer(id) {
        return (await this.load()).dealerIdToConfig[id];
    }
    async getDealers() {
        return (await this.load()).dealerIdToConfig;
    }
    getFeatureToggles() {
        var _a;
        return (_a = this.options.featureToggles) !== null && _a !== void 0 ? _a : {};
    }
    getDepartmentFallbackOrder() {
        const result = this.options.departmentFallbackOrder;
        if (result && Array.isArray(result) && result.length > 0) {
            return result;
        }
        return ['SALES', 'SALES_USED', 'SERVICE'];
    }
    getServerTime() {
        return this.options.serverTime;
    }
    subscribe(listener) {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }
    getPageId() {
        const currentPath = this.historyService.rootHistory.location.pathname;
        return this._getPageId(currentPath);
    }
    _getPageId(path) {
        const match = Object.entries(this.options.pageIdToSubPagePath).find((entry) => {
            if (!entry) {
                return false;
            }
            const [_, pagePath] = entry;
            const queryOffset = pagePath.indexOf('?');
            const cleansedPath = queryOffset === -1 ? pagePath : pagePath.substring(0, queryOffset);
            return path.includes(cleansedPath);
        });
        if (!match) {
            return undefined;
        }
        const [pageId, ..._rest] = match;
        return pageId;
    }
    async getOwnerByPath(path) {
        const dealerHome = this.getDealerHomePath(path);
        if (dealerHome) {
            return (await this.load()).pathToDealerId[dealerHome];
        }
        return undefined;
    }
    async load() {
        if (!this.globalDealerConfigPromise) {
            this.globalDealerConfigPromise = new Promise((resolve, reject) => {
                const dealerConfigMap = {
                    dealerIdToConfig: {},
                    pathToDealerId: {},
                };
                this.options
                    .fetchGlobalConfig()
                    .then(({ dealerMap, serverTime }) => {
                    // update server-time
                    this.options.serverTime = serverTime;
                    // update dealer configs
                    Object.keys(dealerMap).forEach((dealerId) => {
                        const dealerConfig = dealerMap[dealerId];
                        dealerConfigMap.dealerIdToConfig[dealerId] =
                            this.createDealerConfiguration(dealerId, dealerConfig);
                        dealerConfigMap.pathToDealerId[dealerConfig.appendableHomePath] =
                            dealerId;
                    });
                    resolve(dealerConfigMap);
                })
                    .catch((e) => reject(e));
            });
        }
        return this.globalDealerConfigPromise;
    }
    createDealerConfiguration(dealerId, dealerConfig) {
        const displayName = dealerConfig.displayName || dealerConfig.legalName;
        return {
            createHomeNavigation: createHomeNavigationFactory(dealerConfig, this.historyService, this.options.pageIdToSubPagePath, this.options.onehubHost),
            displayName,
            legalName: dealerConfig.legalName,
            firstLevelNavigationName: dealerConfig.firstLevelNavigationName || displayName,
            headlessPath: dealerConfig.headlessPath,
            id: dealerId,
            topbarName: dealerConfig.topbarName || displayName,
            trackingDisabled: dealerConfig.trackingDisabled === true,
            dealerType: dealerConfig.dealerType || 'FULLSTACK',
        };
    }
    getCurrentDealerHome() {
        const pathname = this.historyService.rootHistory.location.pathname;
        return this.getDealerHomePath(pathname);
    }
    getDealerHomePath(path) {
        if (path.startsWith(this.options.dealerBasePath)) {
            const p = path
                .substring(this.options.dealerBasePath.length)
                .split(/[\/|\.]/);
            if (p.length > 1) {
                return this.options.dealerBasePath + '/' + p[1];
            }
        }
        return undefined;
    }
}
exports.MyDealerConfigurationServiceV2Impl = MyDealerConfigurationServiceV2Impl;
const createHomeNavigationFactory = (dealerConfig, historyService, pageIdToSubPagePath, onehubHost) => (subpageId) => {
    const rootHistory = historyService.rootHistory;
    const pathname = subpageId
        ? dealerConfig.appendableHomePath + (pageIdToSubPagePath[subpageId] || '')
        : dealerConfig.homePath;
    if (Boolean(onehubHost)) {
        // on integrator pages a history push won't work. The browser needs to load the page new.
        const fullyQualifiedPathName = onehubHost + pathname;
        return {
            createHref: () => rootHistory.createHref({ pathname: fullyQualifiedPathName }),
            push: () => (window.location.href = fullyQualifiedPathName),
            replace: () => (window.location.href = fullyQualifiedPathName),
        };
    }
    const location = { pathname };
    return {
        createHref: () => rootHistory.createHref(location),
        push: () => rootHistory.push(location),
        replace: () => rootHistory.replace(location),
    };
};
//# sourceMappingURL=MyDealerConfigurationServiceV2Impl.js.map